import URL from 'url-parse';

import * as Actions from '../../actions/bookboost/proactive-message-actions';
import { loadState, getProactiveMessageStorageKey } from '../../utils/bookboost/storage';
import { removeTrailingSlash } from '../../utils/bookboost/string';

const INITIAL_STATE = loadState(getProactiveMessageStorageKey()) || {
    active: [],
    inactive: [],
};
// Location should not be loaded from localstorage
INITIAL_STATE.location = parent.location.href;

const createButton = (button) => {
    if (button.type === 'coupon') {
        return {
            ...button,
            couponRead: false
        };
    }
    return button;
};

export const createMessage = ({ id, message }) => ({
    ...message,
    id,
    button: createButton(message.button),
    closed: false,
    closedAt: null,
    activeAt: null
});

const readCoupon = (message, action) => {
    if (message.id === action.id) {
        return {
            ...message,
            button: {
                ...message.button,
                couponRead: true
            }
        };
    }
    return message;
};

const activateMessage = (state, action) => {
    const { active, inactive, ...rest } = state;

    const index = inactive.findIndex((message) => message.id === action.id);

    if (index !== -1) {
        const message = { ...inactive[index], activeAt: action.time };
        return {
            ...rest,
            active: [...active, message],
            inactive: [...inactive.slice(0, index), ...inactive.slice(index + 1)]
        };
    }
    return state;
};

export const closeMessage = (message) => {
    return {
        ...message,
        closed: true,
        closedAt: Date.now()
    };
};

const closeMessageReducer = (message, action) => {
    if (message.id === action.id) {
        return closeMessage(message);
    }
    return message;
};

const clearExpiredMessages = ({ active, ...rest }) => {
    const now = Date.now();
    const hour = 60 * 60 * 1000;
    const autoCloseLimit = 1 * hour;
    const reopenLimit = 24 * hour;

    const autoClose = (message) => {
        if (!message.closed && (now - message.activeAt) > autoCloseLimit) {
            return closeMessage(message);
        }
        return message;
    }
    const canReopen = ({ closedAt }) => (closedAt && (now - closedAt) > reopenLimit);

    return {
        ...rest,
        active: active.map(autoClose).filter((m) => !canReopen(m))
    };
};

const checkTargetUrl = (location, target) => {
    const currentUrl = new URL(removeTrailingSlash(location));
    const baseUrl = location;
    const targetUrl = new URL(removeTrailingSlash(target.url), baseUrl);
    return targetUrl.host === currentUrl.host && targetUrl.pathname === currentUrl.pathname;
};

const registerProactiveMessages = ({ active, inactive, location, ...rest }, { proactiveMessages }) => {
    return {
        ...rest,
        active: active
            // Filter out active messages that are no longer available from backend.
            .filter(({ id: id1 }) => proactiveMessages.some(({ id: id2 }) => id1 === id2)),
        inactive: proactiveMessages
            // Filter out messages that should not trigger on this url.
            .filter(({ targets }) => {
                if (!targets || !targets.length) {
                    return true;
                }
                return targets.some((target) => {
                    switch (target.type) {
                        case 'url':
                            return checkTargetUrl(location, target);
                        default:
                            return false;
                    }
                });
            })
            // Filter out messages that are already active.
            .filter(({ id: id1 }) => active.every(({ id: id2 }) => id1 !== id2))
            .map(createMessage),
        location
    };
};

export const ProactiveMessageReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case Actions.ACTIVATE_MESSAGE: return activateMessage(state, action);

        case Actions.READ_COUPON:
            return {
                ...state,
                active: state.active.map((message) => readCoupon(message, action))
            };

        case Actions.CLOSE_MESSAGE:
            return {
                ...state,
                active: state.active.map((message) => closeMessageReducer(message, action))
            };

        case Actions.CLEAR_EXPIRED_MESSAGES: return clearExpiredMessages(state, action);

        case Actions.REGISTER_PROACTIVE_MESSAGES: return registerProactiveMessages(state, action);

        default:
            return state;
    }
};
